import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  createRaccourci,
  deleteMenu,
  getMenus,
  importMenusFromExcel,
} from "../Services/MenuApi";
import swal from "sweetalert";
import { getOrganisationById } from "../Services/OrganizationApi";
import { useUser } from "../Auth/useUser";
import GifLoader from "../Components/loader";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";

// React DnD
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useOrganisations } from "../Auth/useOrganisations";
import { getImageUrl } from "../Helpers/Helpers";

// Item type for drag and drop
const ItemTypes = {
  PRODUCT: "product",
};

// Draggable Product
const DraggableProduct = React.memo(({ product, currency }) => {
  const navigate = useNavigate();

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: ItemTypes.PRODUCT,
      item: { product },
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging(),
      }),
    }),
    [product],
  );

  const titre =
    typeof product.titre === "object" ? product.titre.fr : product.titre;

  return (
    <div
      ref={drag}
      className="box"
      style={{
        height: "150px",
        width: "150px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab",
      }}
      onClick={() => navigate("/menu/edit/" + product.id)}
    >
      {product.image && product.image.length ? (
        <img
          src={getImageUrl(product.image)}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "images/product/no-image.png";
          }}
          alt={titre}
          style={{
            width: "80px",
            height: "80px",
            objectFit: "cover",
            borderRadius: "8px",
            marginBottom: "10px",
          }}
        />
      ) : (
        <div
          style={{
            width: "80px",
            height: "80px",
            backgroundColor: "#e9ecef",
            borderRadius: "8px",
            marginBottom: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <i className="fa fa-image fa-2x text-muted"></i>
        </div>
      )}
      <h6 className="text-truncate" style={{ maxWidth: "150px" }}>
        {titre}
      </h6>
      {product.prix_ttc && (
        <p className="text-success mb-0">
          <strong>
            {product.prix_ttc} {currency}
          </strong>
        </p>
      )}
    </div>
  );
});

// Droppable Placeholder Slot
const PlaceholderSlot = React.memo(
  ({ index, product, onDrop, onRemove, currency }) => {
    const [{ isOver }, drop] = useDrop(
      () => ({
        accept: ItemTypes.PRODUCT,
        drop: (item) => onDrop(item.product, index),
        collect: (monitor) => ({
          isOver: !!monitor.isOver(),
        }),
      }),
      [index, onDrop],
    );

    const titre = product
      ? typeof product.titre === "object"
        ? product.titre.fr
        : product.titre
      : "";

    return (
      <div
        ref={drop}
        className="box"
        style={{
          border: isOver ? "3px dashed #007bff" : "2px dashed #dee2e6",
          backgroundColor: isOver ? "#f0f8ff" : "#f8f9fa",
          height: "150px",
          width: "150px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.3s ease",
          position: "relative",
          cursor: "pointer",
        }}
      >
        {product ? (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              className="btn btn-sm btn-danger"
              style={{
                position: "absolute",
                top: "5px",
                right: "5px",
                zIndex: 10,
                width: "25px",
                height: "25px",
                padding: "0",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>

            {product.image && product.image.length ? (
              <img
                src={getImageUrl(product.image)}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "images/product/no-image.png";
                }}
                alt={titre}
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  marginBottom: "10px",
                }}
              />
            ) : (
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  backgroundColor: "#e9ecef",
                  borderRadius: "8px",
                  marginBottom: "10px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fa fa-image fa-2x text-muted"></i>
              </div>
            )}

            <h6 className="text-truncate" style={{ maxWidth: "150px" }}>
              {titre}
            </h6>
            {product.prix_ttc && (
              <p className="text-success mb-0">
                <strong>
                  {product.prix_ttc} {currency}
                </strong>
              </p>
            )}
          </>
        ) : (
          <div className="text-center text-muted">
            <i className="fa fa-plus-circle fa-3x mb-3"></i>
            <h6>Slot {index + 1}</h6>
            <p className="small mb-0">Déposer un produit ici</p>
          </div>
        )}
      </div>
    );
  },
);

function MenuList() {
  const { t } = useTranslation();
  const user = useUser();
  const [
    organisations,
    setOrganisations,
    usedOrganisations,
    setUsedOrganisations,
  ] = useOrganisations();

  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("");
  const [filter, setFilter] = useState("");
  const [rand, setRand] = useState(0);
  // Inside MenuList.jsx
  const [searchTerm, setSearchTerm] = useState(""); // Text used for the API call
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  // Raccourci mode
  const [isRaccourciMode, setIsRaccourciMode] = useState(false);
  const [shortcuts, setShortcuts] = useState(Array(12).fill(null));
  const [raccourciName, setRaccourciName] = useState("Nom de raccourci");

  // Fetch menus with pagination
  const fetchMenus = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        // We now pass searchTerm instead of the raw input 'filter'
        const res = await getMenus(
          usedOrganisations,
          page,
          itemsPerPage,
          searchTerm,
        );
        if (res.data.success) {
          setMenus(res.data.data);
          setTotalPages(res.data.pagination.totalPages);
        }
      } catch (err) {
        console.error("Error fetching menus:", err);
      } finally {
        setLoading(false);
      }
    },
    [usedOrganisations, itemsPerPage, searchTerm], // Depends on searchTerm now
  );

  // Load organisation & menus
  useEffect(() => {
    fetchMenus(currentPage);

    getOrganisationById(usedOrganisations).then((res) => {
      if (res.data.success !== "0") {
        setCurrency(res.data.data.currency);
      }
    });
  }, [fetchMenus, currentPage, usedOrganisations, rand]);

  // Load shortcuts after menus
  useEffect(() => {
    const savedShortcuts = localStorage.getItem(
      `shortcuts_${usedOrganisations}`,
    );
    if (savedShortcuts) {
      try {
        const parsed = JSON.parse(savedShortcuts);
        setShortcuts(
          parsed.map(
            (item) =>
              menus.find((menu) => menu.id === item?.product_id) || null,
          ),
        );
      } catch (err) {
        console.error("Error parsing shortcuts:", err);
        setShortcuts(Array(12).fill(null));
      }
    }
  }, [menus, usedOrganisations]);

  // Pagination helpers
  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Raccourci handlers
  const handleDrop = (product, slotIndex) => {
    const alreadyExistsIndex = shortcuts.findIndex(
      (s, idx) => s && s.id === product.id && idx !== slotIndex,
    );

    if (alreadyExistsIndex !== -1) {
      swal(
        "Produit déjà ajouté!",
        "Ce produit existe déjà dans la grille de raccourcis.",
        "warning",
      );
      return;
    }

    const newShortcuts = [...shortcuts];
    newShortcuts[slotIndex] = product;
    setShortcuts(newShortcuts);
  };

  const removeShortcut = (slotIndex) => {
    const newShortcuts = [...shortcuts];
    newShortcuts[slotIndex] = null;
    setShortcuts(newShortcuts);
  };

  const clearShortcuts = () => {
    swal({
      title: "Effacer tous les raccourcis?",
      text: "Tous les raccourcis seront supprimés.",
      icon: "warning",
      buttons: ["Annuler", "Effacer"],
      dangerMode: true,
    }).then((willClear) => {
      if (willClear) setShortcuts(Array(12).fill(null));
    });
  };

  const saveShortcuts = async () => {
    if (!raccourciName.trim()) {
      swal(
        "Nom manquant!",
        "Veuillez entrer un nom pour votre raccourci",
        "warning",
      );
      return;
    }

    try {
      const validShortcuts = shortcuts
        .map((shortcut, index) =>
          shortcut
            ? {
                position: index,
                product_id: shortcut.id,
                product_name:
                  typeof shortcut.titre === "object"
                    ? shortcut.titre.fr
                    : shortcut.titre,
                product_image: shortcut.image?.[0] || null,
                product_price: shortcut.prix_ttc,
              }
            : null,
        )
        .filter(Boolean);

      if (validShortcuts.length === 0) {
        swal(
          "Aucun raccourci!",
          "Veuillez ajouter au moins un produit à la grille",
          "warning",
        );
        return;
      }

      const raccourciData = {
        raccourci_name: raccourciName,
        shortcuts: validShortcuts,
        organisation_id: usedOrganisations,
        user_id: user?.result?.id || null,
      };

      const res = await createRaccourci(raccourciData);

      if (res.data.success) {
        localStorage.setItem(
          `shortcuts_${usedOrganisations}_${raccourciName}`,
          JSON.stringify(validShortcuts),
        );
        setIsRaccourciMode(false);
        swal(
          "Succès!",
          `La grille "${raccourciName}" a été sauvegardée`,
          "success",
        );
      }
    } catch (err) {
      console.error("Error saving raccourcis:", err);
      swal("Erreur!", "Impossible de sauvegarder les raccourcis", "error");
    }
  };

  const cancelRaccourciMode = () => {
    swal({
      title: "Annuler les modifications?",
      text: "Tous les changements non sauvegardés seront perdus.",
      icon: "warning",
      buttons: ["Continuer l'édition", "Annuler"],
      dangerMode: true,
    }).then((cancel) => {
      if (cancel) setIsRaccourciMode(false);
    });
  };

  // Filtered menus
  const filteredMenus = menus.filter((menu) => {
    const titre = typeof menu.titre === "object" ? menu.titre.fr : menu.titre;
    return titre.toLowerCase().includes(filter.toLowerCase());
  });
  const handleExcelImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [".xlsx", ".xls"];
    const fileExtension = file.name.split(".").pop().toLowerCase();

    if (!validTypes.includes(`.${fileExtension}`)) {
      swal(
        "Format invalide!",
        "Veuillez sélectionner un fichier Excel (.xlsx ou .xls)",
        "error",
      );
      e.target.value = "";
      return;
    }

    try {
      setLoading(true);

      // Prepare form data
      const formData = new FormData();
      formData.append("file", file);

      // Ensure organisation_id is a clean string
      const orgId =
        typeof usedOrganisations === "object"
          ? usedOrganisations._id
          : usedOrganisations;
      formData.append("organisation_id", orgId);

      // Pass the ENTIRE formData to the service
      const res = await importMenusFromExcel(formData);

      if (res.data.success) {
        swal({
          title: "Succès!",
          // Use the length of products returned from the API
          text: `${res.data.data?.length || 0} articles importés avec leurs catégories`,
          icon: "success",
          timer: 3000,
        });
        setRand((r) => r + 1); // Refresh the list
      }
    } catch (err) {
      swal(
        "Erreur!",
        err.response?.data?.message || err.message || "Impossible d'importer",
        "error",
      );
    } finally {
      setLoading(false);
      e.target.value = ""; // Clear input so user can upload the same file again if they fix a mistake
    }
  };
  const downloadTemplate = () => {
    const headers = [
      [
        "titre",
        "categorie",
        "prix_ttc",
        "tva",
        "quantite_stock",
        "code_erp",
        "reference",
        "image",
        "code_a_barre",
      ],
      // [
      //   "Pizza Margherita",
      //   "Pizza",
      //   12.5,
      //   19,
      //   50,
      //   "ERP001",
      //   "REF001",
      //   "pizza.jpg",
      //   "1234567890123",
      // ], // optional example row
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(headers);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Produits");

    XLSX.writeFile(workbook, "modele_import_produits.xlsx");
  };
  return (
    <DndProvider backend={HTML5Backend}>
      <section className="content">
        <GifLoader loading={loading} />

        {/* Header */}
        <div className="row mb-4">
          <div className="col-md-4">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSearchTerm(filter); // Trigger the search
                setCurrentPage(1); // Reset to page 1 for new search
              }}
            >
              <div className="input-group">
                <input
                  type="search"
                  className="form-control m-0"
                  placeholder="Recherche ..."
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)} // Just updates text, no API call yet
                />
                <button className="btn btn-primary" type="submit">
                  <i className="fa fa-search"></i>
                </button>
                {searchTerm && (
                  <button
                    className="btn btn-warning"
                    type="button"
                    onClick={() => {
                      setFilter("");
                      setSearchTerm("");
                      setCurrentPage(1);
                    }}
                  >
                    <i className="fa fa-times"></i>
                  </button>
                )}
              </div>
            </form>
          </div>
          <div className="col-md-8 text-end">
            <div className="d-flex gap-2">
              {isRaccourciMode ? (
                <>
                  <button
                    className="btn btn-success m-1"
                    onClick={saveShortcuts}
                  >
                    Sauvegarder
                  </button>
                  <button
                    className="btn btn-warning m-1"
                    onClick={clearShortcuts}
                  >
                    Effacer tout
                  </button>
                  <button
                    className="btn btn-secondary"
                    onClick={cancelRaccourciMode}
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-info m-1"
                    onClick={() => navigate("/categories")}
                  >
                    {t("Catégories")}
                  </button>
                  <button
                    className="btn btn-danger m-1"
                    onClick={() => setIsRaccourciMode(true)}
                  >
                    {t("Ajouter raccourci")}
                  </button>
                  <button
                    className="btn btn-warning m-1"
                    onClick={() => navigate("/raccourcis")}
                  >
                    Raccourcis
                  </button>
                  <button
                    className="btn btn-success m-1"
                    onClick={() => navigate("/menus/add")}
                  >
                    {t("Ajouter")}
                  </button>
                  <label className="btn btn-primary m-1">
                    Importer Excel
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleExcelImport}
                      style={{ display: "none" }}
                    />
                  </label>{" "}
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={downloadTemplate}
                  >
                    <i className="fas fa-file-download"></i> Télécharger le
                    modèle Excel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Products & Raccourci Grid */}
        {/* In raccourci mode, `position: sticky` can't be used to keep the
            slots grid in view: this app's `.content-wrapper` has
            `overflow: hidden` (see public/css/style.css), which makes it the
            nearest "scrolling" ancestor for sticky purposes even though it
            never actually scrolls itself (the window does) — so sticky
            descendants never engage and just scroll away with the page.
            Instead, both columns get their own bounded, independently
            scrolling box: the product list scrolls internally while the
            slots grid — short enough to always fit — stays fully visible. */}
        <div
          className="row"
          style={
            isRaccourciMode
              ? { maxHeight: "calc(100vh - 230px)" }
              : undefined
          }
        >
          <div
            className={isRaccourciMode ? "col-md-6" : "col-12"}
            style={
              isRaccourciMode
                ? { maxHeight: "calc(100vh - 230px)", overflowY: "auto" }
                : undefined
            }
          >
            <div className="row">
              {menus.map((menu, idx) => (
                <div
                  key={idx}
                  className={
                    isRaccourciMode
                      ? "col-3"
                      : "col-xxxl-2 col-xl-3 col-lg-4 col-12"
                  }
                >
                  <DraggableProduct product={menu} currency={currency} />
                </div>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="row mt-4">
                <div className="col-12">
                  <div className="d-flex justify-content-end">
                    <nav aria-label="Page navigation">
                      <ul className="pagination mb-0">
                        <li
                          className={`page-item ${
                            currentPage === 1 ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            <i className="fa fa-chevron-left"></i>
                          </button>
                        </li>

                        {[...Array(totalPages)].map((_, i) => {
                          const pageNum = i + 1;
                          if (
                            pageNum === 1 ||
                            pageNum === totalPages ||
                            (pageNum >= currentPage - 2 &&
                              pageNum <= currentPage + 2)
                          ) {
                            return (
                              <li
                                key={pageNum}
                                className={`page-item ${
                                  currentPage === pageNum ? "active" : ""
                                }`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() => handlePageChange(pageNum)}
                                >
                                  {pageNum}
                                </button>
                              </li>
                            );
                          } else if (
                            pageNum === currentPage - 3 ||
                            pageNum === currentPage + 3
                          ) {
                            return (
                              <li key={pageNum} className="page-item disabled">
                                <span className="page-link">...</span>
                              </li>
                            );
                          }
                          return null;
                        })}

                        <li
                          className={`page-item ${
                            currentPage === totalPages ? "disabled" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            <i className="fa fa-chevron-right"></i>
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>

                  <div className="text-right text-muted mt-2">
                    Page {currentPage} sur {totalPages} - {itemsPerPage}{" "}
                    produits par page
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Raccourci Grid */}
          {isRaccourciMode && (
            <div
              className="col-md-6"
              style={{ maxHeight: "calc(100vh - 230px)", overflowY: "auto" }}
            >
              <input
                type="text"
                className="form-control mb-3"
                value={raccourciName}
                onChange={(e) => setRaccourciName(e.target.value)}
                placeholder="Nom de la grille..."
              />
              <div className="row">
                {Array.from({ length: 12 }).map((_, idx) => (
                  <div key={idx} className="col-3 mb-3">
                    <PlaceholderSlot
                      index={idx}
                      product={shortcuts[idx]}
                      onDrop={handleDrop}
                      onRemove={removeShortcut}
                      currency={currency}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </DndProvider>
  );
}

export default MenuList;